import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import { useDataQuery } from "@hooks/useData/useDataQuery";
import { authClient } from "@utils/authClient";
import { selectData, softDelete, supabase } from "@utils/serverClient";
import { Playlist, UpdatePlaylist } from "types";
import useGzLog from "@hooks/useGzLog";
import useDialog from "@hooks/useDialog";
import { PlaylistItem } from "@components/Playlist/PlaylistsPageComponents/AddContent";
import { groupBy } from "@utils/index";
import { TransitionDbType } from "@components/Playlist/PlaylistsPageComponents/transitionVariants";
import { useScreens } from "@hooks/useScreens";
import { undefinedToNull } from "lib/utils";

export const usePlaylists = () => {
  const { organization } = useAuth();
  const orgId = organization?.id;
  const queryKey = ["playlists", orgId];
  const { log } = useGzLog();
  const { showErrorSnackbar } = useDialog();
  const { emitEvent, screens } = useScreens();

  const {
    status: playlistsFetchStatus,
    data: playlists,
    refetch: getPlaylists,
  } = useDataQuery({
    queryKey,
    queryFn: async () => {
      if (!orgId) return [];
      const accessToken = await authClient.getAccessToken();

      if (!accessToken) return [];
      const { data, error } = await selectData({
        table: "playlists",
        columns: "*",
      })
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });
      if (error || !data) return [];
      return data;
    },
  });

  const createMutation = useDataMutation({
    mutationFn: async (playlist: Partial<Playlist>) => {
      let playlistAction = supabase.from("playlists")
      const payload = { ...playlist, org_id: orgId, is_smart: true }
      if (playlist.id) {
        playlistAction = playlistAction.upsert(payload)
      } else {
        playlistAction = playlistAction.insert(payload)
      }

      const { data, error } = await playlistAction
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to create playlist.",
    optimisticUpdateFn: (
      oldData: Playlist[] | undefined,
      newPlaylist: Partial<Playlist>,
    ) => {
      const optimisticPlaylist: Playlist = {
        ...undefinedToNull(newPlaylist),
        is_smart: true,
        id: `temp-${Date.now()}`,
        name: newPlaylist.name ?? "New Playlist",
        created_at: new Date().toISOString(),
        org_id: orgId ?? "",
      };
      return [optimisticPlaylist, ...(oldData || [])];
    },
    options: {
      onSuccess: (data) => {
        log({
          eventtype: "PlaylistCreated",
          name: "PlaylistCreated",
          payload: {
            playlistId: data?.id ?? "undefined",
            playlistName: data?.name ?? "undefined",
          },
        });
      },
      onError: (error, newPlaylist) => {
        log({
          eventtype: "PlaylistCreateFailed",
          name: "PlaylistCreateFailed",
          payload: {
            playlistName: newPlaylist.name ?? "undefined",
            errorMessage: String(error?.message || "Unknown error creating playlist"),
          },
        });
      },
    }
  });

  const updateMutation = useDataMutation({
    mutationFn: async (variables: UpdatePlaylist) => {
      const { error, data } = await supabase
        .from("playlists")
        .update(variables)
        .eq("id", variables.id)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update playlist.",
    optimisticUpdateFn: (oldData, variables) => {
      if (!oldData) return [];
      return oldData.map((item) =>
        item.id === variables.id ? { ...item, ...variables } : item,
      );
    },
    options: {
      onSuccess: (data) => {
        if (!data) return
        const _toUpdate = screens.filter((e) => e.playlist_id === data.id);
        for (const screen of _toUpdate) {
          emitEvent("playlistChanged", screen.id);
        }
        log({
          eventtype: "PlaylistUpdated",
          name: "PlaylistUpdated",
          payload: {
            playlistId: data?.id ?? "undefined",
            playlistName: data?.name ?? "undefined",
          },
        });
      },
      onError: (error, variables) => {
        log({
          eventtype: "PlaylistUpdateFailed",
          name: "PlaylistUpdateFailed",
          payload: {
            playlistId: variables.id,
            playlistName: variables.name ?? "undefined",
            errorMessage: String(error?.message || "Unknown error updating playlist"),
          },
        });
      },
    }
  });

  const deleteMutation = useDataMutation({
    mutationFn: async (id: string) => {
      const { error } = await softDelete({
        table: "playlists",
      }).match({ id });

      if (error) {
        throw new Error(error.message);
      }
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete playlist.",
    optimisticUpdateFn: (oldData: Playlist[] | undefined, id) => {
      if (!oldData) return [];
      return oldData.filter((item) => item.id !== id);
    },
    options: {
      onSuccess: (_, id) => {
        log({
          eventtype: "PlaylistDeleted",
          name: "PlaylistDeleted",
          payload: { playlistId: id },
        });
      },
      onError: (error, id) => {
        log({
          eventtype: "PlaylistDeleteFailed",
          name: "PlaylistDeleteFailed",
          payload: {
            playlistId: id,
            errorMessage: String(error?.message || "Unknown error deleting playlist"),
          },
        });
      },
    }
  });

  const orderPlaylistItems = (items: PlaylistItem[]): PlaylistItem[] => {
    const _items = groupBy(items, "playOrder");
    const _cleaned = Object.values(_items)
      .map((val, idx) => val.map((v) => ({ ...v, playOrder: idx + 1 })))
      .flat();
    return _cleaned;
  };

  const addItemToPlaylist = (playlistId: string, item: PlaylistItem) => {
    const playlist = playlists?.find((p) => p.id === playlistId);
    if (!playlist) return;
    const updatedItems = [...(playlist.items as PlaylistItem[] ?? []), item];
    updateMutation.mutate({ id: playlistId, items: orderPlaylistItems(updatedItems) });
  };

  const updatePlaylistItem = (playlistId: string, itemIndex: number, item: PlaylistItem) => {
    const playlist = playlists?.find((p) => p.id === playlistId);
    if (!playlist) return;
    const updatedItems = [...(playlist.items as PlaylistItem[] ?? [])];
    updatedItems[itemIndex] = item;
    updateMutation.mutate({ id: playlistId, items: orderPlaylistItems(updatedItems) });
  };

  const deletePlaylistItem = (playlistId: string, itemId: number) => {
    const playlist = playlists?.find((p) => p.id === playlistId);
    if (!playlist) return;
    const updatedItems = (playlist.items as PlaylistItem[] ?? []).filter((_, i) => i !== itemId);
    updateMutation.mutate({ id: playlistId, items: orderPlaylistItems(updatedItems) });
  };

  const changePlaylistOrder = (playlistId: string, newOrderedItems: PlaylistItem[], transition?: TransitionDbType) => {
    updateMutation.mutate({ id: playlistId, items: orderPlaylistItems(newOrderedItems), transition: transition ? JSON.stringify(transition) : undefined }, {
      onSuccess: () => {
        log({
          eventtype: "PlaylistOrderChanged",
          name: "PlaylistOrderChanged",
          payload: {
            playlistId,
          },
        });
      },
      onError: (error) => {
        showErrorSnackbar("Something went wrong");
        log({
          eventtype: "PlaylistOrderChangeFailed",
          name: "PlaylistOrderChangeFailed",
          payload: {
            playlistId,
            error: error.message,
          },
        });
      }
    });
  };

  const getPlaylistNameById = (id: string): string => {
    const _loc = (playlists ?? []).find((playlist) => playlist.id === id);
    return _loc?.name ?? "N/A";
  };

  return {
    playlists: (playlists as Playlist[]) ?? [],
    playlistsFetchStatus,
    getPlaylists,
    getPlaylistNameById,
    createPlaylist: createMutation.mutate,
    isCreating: createMutation.isPending,
    updatePlaylist: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    deletePlaylist: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    addItemToPlaylist,
    updatePlaylistItem,
    deletePlaylistItem,
    changePlaylistOrder,
  };
};
''
